import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Loader2, X, ChevronLeft, ChevronRight, ChevronDown, Star, Volume2, Lock, Unlock, Globe,
  Image as ImageIcon, Minus, RefreshCw, Sparkles, BookmarkPlus, Pencil,
  Layers, Check, AlertTriangle, MessageSquare, GripHorizontal, Crosshair,
} from 'lucide-react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { WordEntryStatus } from './WordEntryStatus';

const WORD_CATEGORIES = {
  1:'Daily Life',2:'Home and Household',3:'Food and Cooking',4:'Clothing and Accessories',
  5:'Body and Anatomy',6:'Medicine and Healthcare',7:'Psychology and Emotions',
  8:'Family and Relationships',9:'People and Professions',10:'Communication',
  11:'Language and Linguistics',12:'Education and Learning',13:'Society and Culture',
  14:'History and Archaeology',15:'Religion and Spirituality',16:'Philosophy and Ethics',
  17:'Mathematics and Statistics',18:'Physics',19:'Chemistry',20:'Biology and Life Sciences',
  21:'Astronomy and Space',22:'Research and Scientific Methods',23:'Nature and Outdoors',
  24:'Arts and Visual Design',25:'Literature and Writing',26:'Film and Entertainment',
  27:'Crafts and Skilled Trades',28:'Materials and Textiles',29:'Measurements and Units',
  30:'Energy and Power Systems',31:'Energy and Electricity',32:'Computing and Technology',
  33:'Engineering',34:'Mechanics and Tools',35:'Construction and Architecture',
  36:'Cities and Infrastructure',37:'Transportation and Vehicles',38:'Manufacturing and Industry',
  39:'Agriculture and Farming',40:'Business and Management',41:'Economics and Finance',
  42:'Law and Legal Systems',43:'Government and Politics',44:'Military and Defense',
  45:'Public Safety and Security',46:'Geography and Cartography',47:'Earth Science and Geology',
  48:'Weather and Climate',49:'Environment and Ecology',50:'Animals and Zoology',
  51:'Plants and Botany',52:'Travel and Tourism',53:'Sports and Fitness',54:'Games and Recreation',
  55:'Music and Performing Arts',56:'Media and Journalism',57:'Shopping and Commerce',
  58:'Real Estate and Property',59:'Law Enforcement and Criminal Justice',60:'General and Miscellaneous',
};

/* ========================
   Collapsible Section
   ======================== */
function CollapsibleSection({ title, defaultOpen = false, onRefresh, headerExtra, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <section className="forge-section">
      <div className="flex items-center justify-between">
        <button
          className="forge-section-title flex-1 text-left"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={`forge-arrow ${isOpen ? 'open' : ''}`}>&#9656;</span>
          {title}
        </button>
        {headerExtra}
        {onRefresh && (
          <button
            className="forge-refresh-btn"
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            title="Refresh with AI"
          >
            <RefreshCw size={13} />
          </button>
        )}
      </div>
      {isOpen && <div className="forge-section-body">{children}</div>}
    </section>
  );
}

/* ========================
   Word Popover — shared across Reader and VideoReader
   ========================
   Pure presentational component. All state lives in `useWordPopover` +
   the parent page; all actions are callbacks. This way the popover can be
   dropped into any page (Reader, VideoReader, command palette, bible study…)
   with no duplication.
*/
export default function WordPopover({
  style,
  wordData,
  wordLoading,
  token,
  language,
  noteText,
  setNoteText,
  translationText,
  setTranslationText,
  translationSaved,
  onPlayAudio,
  onSetStatus,
  onMarkExpression,
  onRelatedWordClick,
  onPopoverBack,
  historyDepth = 0,
  onSaveTranslation,
  onAutoTranslate,
  onSaveNote,
  onOpenSaveLang,
  onClose,
  onRefreshSection,
  onDeleteDefinition,
  onTogglePreferred,
  onUnlockWord,
  onUploadImage,
  onRemoveImage,
  actionLoading,
  locked,
  isMobile,
  isAdmin,
  isExpression = false,
  hasCachedAudio = false,
  isNewEntry = true,
  // ── Expression Mode (in-popover) ──
  selectedSpan,
  isExpressionMode = false,
  onToggleExpressionMode,
  onExpandLeft,
  onExpandRight,
  onShrinkSelection,
  onSaveExpression,
  onEnrichExpression,
  expressionSaving = false,
  // ── Admin per-field lock + edit/suggest ──
  lockedFields = [],
  onToggleFieldLock,
  onSaveEdit,
  onSubmitSuggestion,
  isModal = false,          // when true: renders relative (inside Dialog), no drag
  sentenceText = '',        // the sentence currently showing in Reader
  userTier = 1,             // subscription tier (3+ = In Context feature)
}) {
  const [showDetails, setShowDetails] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedDefinition, setEditedDefinition] = useState('');
  const [suggestionReason, setSuggestionReason] = useState('');
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [inContextLoading, setInContextLoading] = useState(false);
  const [inContextResult, setInContextResult] = useState(null);
  const [bottomExpanded, setBottomExpanded] = useState(false);
  const fileInputRef = useRef(null);
  const currentStatus = wordData?.user_state?.status || 'new';
  // In Expression Mode, prefer the span's surface form (joined multi-word phrase).
  const singleWord = token?.surface_form?.trim() || '';
  const displayWord = isExpressionMode && selectedSpan?.surfaceForm
    ? selectedSpan.surfaceForm
    : singleWord;
  const word = displayWord;
  const langCode = language?.code || 'en';

  // Reset image-load-failed whenever we look up a different word so the image
  // is given a fresh chance to render.
  useEffect(() => {
    setImageLoadFailed(false);
    setInContextResult(null);
    setBottomExpanded(false);
  }, [word, langCode, wordData?.image_url]);

  // ── Drag state for repositioning the popover ──
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef(null);

  // Reset drag offset when popover reopens for a different word
  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
  }, [word, langCode]);

  const handleDragStart = useCallback((e) => {
    // Works for both mouse and touch
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStart.current = { x: clientX - dragOffset.x, y: clientY - dragOffset.y };
    setIsDragging(true);
    e.preventDefault();
  }, [dragOffset]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setDragOffset({
        x: clientX - dragStart.current.x,
        y: clientY - dragStart.current.y,
      });
    };
    const onEnd = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [isDragging]);

  // Auto-play audio on popover open — sticky user preference.
  const [autoPlayAudio, setAutoPlayAudio] = useState(() => {
    try {
      return localStorage.getItem('iling_popover_auto_play_audio') !== '0';
    } catch (e) {
      return true;
    }
  });
  const autoPlayedRef = useRef(null);
  useEffect(() => {
    // Key so we only fire once per (word + lang) lifecycle.
    const key = `${word}|${langCode}`;
    if (!token || !word || !onPlayAudio || !autoPlayAudio) return;
    if (isExpressionMode) return;  // skip autoplay while selecting a phrase
    if (autoPlayedRef.current === key) return;
    autoPlayedRef.current = key;
    // Defer to next tick so the popover is fully painted. NOTE: do NOT
    // return a cleanup that clears this timer — ``token`` gets a fresh
    // object reference when enrichment merges in, triggering re-render +
    // cleanup before 180 ms elapses, which swallowed autoplay entirely.
    setTimeout(() => {
      try { onPlayAudio(); } catch (e) { console.warn('[WordPopover autoplay] error', e); }
    }, 180);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, word, langCode, autoPlayAudio]);

  const toggleAutoPlay = () => {
    setAutoPlayAudio((v) => {
      const next = !v;
      try { localStorage.setItem('iling_popover_auto_play_audio', next ? '1' : '0'); } catch (e) { /* ignore */ }
      return next;
    });
  };

  // ── Global keyboard shortcuts (X / R / F / K / Esc) ──
  // Lives in the shared component so Reader, VideoReader, and CommandPalette
  // all get them for free. Skips when focus is on an input / textarea /
  // contenteditable so users can still type notes/translations.
  useEffect(() => {
    if (!token) return;
    const handler = (e) => {
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || t.isContentEditable) return;

      const key = (e.key || '').toLowerCase();
      let next = null;
      // In Expression Mode the X/R/F/K status keys don't apply to the span.
      if (isExpressionMode && ['x', 'r', 'f', 'k'].includes(key)) return;
      switch (key) {
        case 'x': next = 'ignored'; break;
        case 'r': next = 'learning'; break;
        case 'f': next = 'familiar'; break;
        case 'k': next = 'known'; break;
        case 'escape':
          if (onClose) { e.preventDefault(); onClose(); }
          return;
        default:
          return;
      }
      if (next && onSetStatus && !actionLoading) {
        e.preventDefault();
        onSetStatus(next);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [token, onSetStatus, onClose, actionLoading, isExpressionMode]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadImage) return;
    setUploadingImage(true);
    try {
      await onUploadImage(file);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const searchLinks = [
    { label: 'Google Transl.', url: `https://translate.google.com/?sl=${langCode}&tl=en&text=${encodeURIComponent(word)}` },
    { label: 'Bing Transl.',   url: `https://www.bing.com/translator?from=${langCode}&to=en&text=${encodeURIComponent(word)}` },
    { label: 'Google Search',  url: `https://www.google.com/search?q=${encodeURIComponent(word)}` },
    { label: 'Reverso',        url: `https://context.reverso.net/translation/${language?.name?.toLowerCase() || 'german'}-english/${encodeURIComponent(word)}` },
  ];

  return (
    <div
      data-popover-panel="true"
      data-testid="word-popover"
      className="forge-popover z-[9999]"
      style={isModal ? {
        position: 'relative',
        width: '100%',
        maxHeight: '72vh',
        display: 'flex',
        flexDirection: 'column',
      } : {
        ...style,
        position: 'fixed',
        width: isMobile ? 'calc(100vw - 20px)' : 380,
        maxWidth: isMobile ? 'calc(100vw - 20px)' : 380,
        maxHeight: style?.maxHeight || '85vh',
        display: 'flex',
        flexDirection: 'column',
        transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        cursor: isDragging ? 'grabbing' : undefined,
      }}
    >
      {/* Drag handle — hidden in modal mode */}
      {!isMobile && !isModal && (
        <div
          className="flex items-center justify-center py-1 cursor-grab active:cursor-grabbing shrink-0 select-none hover:bg-muted/30 transition-colors rounded-t-2xl"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          data-testid="popover-drag-handle"
          title="Drag to reposition"
        >
          <GripHorizontal size={14} className="text-muted-foreground/40" />
        </div>
      )}
      {wordLoading ? (
        <div className="flex items-center justify-center p-8" data-testid="word-popover-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (wordData || isExpressionMode) ? (
        <>
          <WordEntryStatus data={wordData} />
          {/* ── Word Image — full square at the very top ── */}
          {wordData?.image_url && !imageLoadFailed && (
            <div className="relative flex-shrink-0">
              <img
                src={wordData.image_url}
                alt={word}
                className="w-full aspect-square object-cover"
                style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16, display: 'block' }}
                onError={() => setImageLoadFailed(true)}
              />
              {isAdmin && onRemoveImage && (
                <button
                  onClick={onRemoveImage}
                  className="absolute top-2 right-2 p-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-full shadow-lg"
                  title="Remove image"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          {/* ── Scrollable content ── */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ minHeight: 0 }}>
            {/* Header */}
            <div className="forge-header">
              {onPopoverBack && historyDepth > 0 && (
                <button
                  onClick={onPopoverBack}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2 px-2 py-1 rounded hover:bg-white/5"
                  data-testid="popover-back-btn"
                  title="Back to previous word"
                >
                  <ChevronLeft size={14} /> Back
                </button>
              )}
              {/* Expression Mode badge (shows # of words selected) */}
              {isExpressionMode && (
                <div className="flex items-center justify-center gap-1 mb-2" data-testid="expression-mode-badge">
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                    <Layers size={12} />
                    Expression Mode ({selectedSpan?.tokens?.length || 1} words)
                  </span>
                </div>
              )}
              <div className="forge-word-row" style={{ alignItems: 'center' }}>
                {/* Left expand chevron (expression mode only) */}
                {isExpressionMode && onExpandLeft && (
                  <button
                    className="forge-expand-btn"
                    title="Expand selection left"
                    onClick={onExpandLeft}
                    data-testid="expand-left-btn"
                  >
                    <ChevronLeft size={18} />
                  </button>
                )}
                {language?.name && (
                  <span
                    className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-500/20 text-blue-400 rounded mr-2 uppercase"
                    title={`Language: ${language.name}`}
                    data-testid="language-badge"
                  >
                    {language.code?.substring(0, 3) || 'EN'}
                  </span>
                )}
                {isNewEntry && !isExpressionMode && (
                  <span className="text-amber-400 mr-1" title="New entry (not in database)">
                    <Star size={18} fill="currentColor" />
                  </span>
                )}
                <h1
                  className={`forge-word-title ${isExpressionMode ? 'expression-title' : ''}`}
                  data-testid="popover-word"
                  style={isExpressionMode ? { fontSize: '1.1rem', lineHeight: '1.3' } : {}}
                >
                  {word}
                </h1>
                {/* Right expand chevron (expression mode only) */}
                {isExpressionMode && onExpandRight && (
                  <button
                    className="forge-expand-btn"
                    title="Expand selection right"
                    onClick={onExpandRight}
                    data-testid="expand-right-btn"
                  >
                    <ChevronRight size={18} />
                  </button>
                )}
                {onRelatedWordClick && !isExpressionMode && (
                  <button
                    className="ml-2 opacity-50 hover:opacity-100 transition-opacity"
                    title="Edit segment — look up a different word or merge characters if the tokenizer split them wrong"
                    onClick={() => {
                      const edited = window.prompt(
                        'Edit the word/segment to look up (e.g. merge split characters or fix tokenizer mistakes):',
                        word
                      );
                      const trimmed = (edited || '').trim();
                      if (trimmed && trimmed !== word) {
                        onRelatedWordClick(trimmed);
                      }
                    }}
                    data-testid="edit-segment-btn"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {isExpression && !isExpressionMode && (
                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-purple-500/20 text-purple-400 rounded" title="Expression (multi-word)">
                    EXPR
                  </span>
                )}
                {onPlayAudio && !isExpressionMode && (
                  <button className="forge-speaker-btn" title="Play audio" onClick={onPlayAudio} data-testid="play-audio-btn">
                    <Volume2 size={22} />
                    {hasCachedAudio && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full" title="Audio cached" />
                    )}
                  </button>
                )}
                {onPlayAudio && !isExpressionMode && (
                  <button
                    className={`ml-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${autoPlayAudio ? 'bg-primary/20 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                    title={autoPlayAudio ? 'Auto-play audio on open (click to turn off)' : 'Auto-play audio on open (click to turn on)'}
                    onClick={toggleAutoPlay}
                    data-testid="auto-play-toggle"
                  >
                    AUTO
                  </button>
                )}
              </div>
              {/* Shrink + Save Expression + AI Fill (expression mode only) */}
              {isExpressionMode && onShrinkSelection && (
                <div className="flex justify-center mt-1">
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-2 py-1 rounded hover:bg-muted/50 transition-colors"
                    onClick={onShrinkSelection}
                    title="Shrink selection"
                    data-testid="shrink-selection-btn"
                  >
                    <Minus size={12} /> Shrink
                  </button>
                </div>
              )}
              {isExpressionMode && (selectedSpan?.tokens?.length ?? 0) > 1 && (
                <div className="flex justify-center gap-2 mt-2 px-2">
                  {onSaveExpression && (
                    <button
                      className="flex-1 text-xs bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors"
                      onClick={onSaveExpression}
                      disabled={expressionSaving}
                      title="Save as expression"
                      data-testid="save-expression-popover-btn"
                    >
                      {expressionSaving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      Save Expression
                    </button>
                  )}
                  {onEnrichExpression && (
                    <button
                      className="flex-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors"
                      onClick={onEnrichExpression}
                      disabled={expressionSaving}
                      title="Fill with AI"
                      data-testid="enrich-expression-popover-btn"
                    >
                      {expressionSaving ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      AI Fill
                    </button>
                  )}
                </div>
              )}
              {(wordData?.ipa || wordData?.zhuyin || wordData?.jyutping) && !isExpressionMode && (
                <div className="forge-phonetics flex flex-col gap-0.5 text-xs leading-tight">
                  {wordData.ipa && (
                    <div data-testid="phonetic-pinyin">
                      <span className="opacity-60 mr-1">
                        {(langCode === 'zh' || langCode === 'zho' || langCode === 'cmn') ? 'Pinyin:' : ''}
                      </span>
                      <span>/{wordData.ipa}/</span>
                    </div>
                  )}
                  {wordData.zhuyin && (
                    <div data-testid="phonetic-zhuyin">
                      <span className="opacity-60 mr-1">Zhuyin:</span>
                      <span>{wordData.zhuyin}</span>
                    </div>
                  )}
                  {wordData.jyutping && (
                    <div data-testid="phonetic-jyutping">
                      <span className="opacity-60 mr-1">Jyutping:</span>
                      <span>{wordData.jyutping}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="forge-top-icons">
                {locked && (
                  <span className="text-amber-400 mr-1" title="Word is locked (admin-verified)">
                    <Lock size={14} />
                  </span>
                )}
                <button className="forge-close-btn forge-close-btn--close" title="Close" onClick={onClose} data-testid="close-popover-btn">&times;</button>
                {onOpenSaveLang && (
                  <button
                    className="forge-close-btn forge-close-btn--lang"
                    title="Save word in another language"
                    onClick={onOpenSaveLang}
                    data-testid="save-lang-icon"
                    style={{ padding: 0, fontSize: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Globe size={18} />
                  </button>
                )}
              </div>
            </div>

            <div className="forge-divider" />

            {/* Admin: browse image (only when no image, or image failed to load) */}
            {isAdmin && wordData && !isExpressionMode && (!wordData.image_url || imageLoadFailed) && onUploadImage && (
              <>
                <div className="px-2 py-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="w-full py-2 px-3 text-sm border border-dashed rounded-lg text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors flex items-center justify-center gap-2"
                    data-testid="browse-image-btn"
                  >
                    {uploadingImage ? (
                      <><Loader2 size={16} className="animate-spin" /> Uploading...</>
                    ) : (
                      <><ImageIcon size={16} /> Browse Image (Admin)</>
                    )}
                  </button>
                </div>
                <div className="forge-divider" />
              </>
            )}

            {/* Expression Mode informational block (shown when wordData is absent) */}
            {isExpressionMode && (
              <div className="px-4 py-3" data-testid="expression-mode-info">
                <p className="text-sm text-muted-foreground text-center">
                  Expression selected: <span className="font-medium text-foreground">{selectedSpan?.tokens?.length || 1} words</span>
                </p>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Use the chevrons above to adjust selection
                </p>
              </div>
            )}

            {/* Quick definitions (collapsed) — preferred defs take precedence */}
            {!showDetails && !isExpressionMode && wordData && (
              <>
                <div className="px-2 py-1">
                  {wordData.definitions?.some(d => d.preferred) ? (
                    wordData.definitions.filter(d => d.preferred).map((def, idx) => (
                      <div key={idx} className="text-sm mb-1" style={{ borderLeft: '2px solid #D4AF37', paddingLeft: 7 }}>
                        {def.pos && <span className="font-medium text-primary mr-1">[{def.pos}]</span>}
                        <span>{def.definition}</span>
                      </div>
                    ))
                  ) : (
                    wordData.definitions?.slice(0, 2).map((def, idx) => (
                      <div key={idx} className="text-sm mb-1">
                        {def.pos && <span className="font-medium text-primary mr-1">[{def.pos}]</span>}
                        <span>{def.definition}</span>
                      </div>
                    ))
                  )}
                  {!wordData.definitions?.length && <p className="text-sm text-muted-foreground">No definitions yet</p>}
                </div>
                <div className="forge-divider" />
              </>
            )}

            {/* Expanded details */}
            {(showDetails || bottomExpanded) && !isExpressionMode && wordData && (
              <>
                <CollapsibleSection title="Preferred definitions:">
                  {wordData.definitions?.filter(d => d.preferred)?.length > 0 ? (
                    wordData.definitions.map((def, idx) => def.preferred ? (
                      <div key={idx} className="forge-field-box" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="forge-entry-line">
                            <span className="forge-pos">{def.pos}</span>
                            <span className="forge-entry-text">{def.definition}</span>
                          </div>
                        </div>
                        {onTogglePreferred && (
                          <button
                            className="forge-remove-btn"
                            title="Remove from preferred"
                            onClick={() => onTogglePreferred(idx)}
                            style={{ flexShrink: 0, marginTop: 2 }}
                            data-testid={`remove-preferred-${idx}`}
                          >
                            <Minus size={12} />
                          </button>
                        )}
                      </div>
                    ) : null)
                  ) : (
                    <p className="forge-empty-hint">Click a definition below to pin it here</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection title="Notes:">
                  <div className="space-y-2">
                    <Textarea
                      placeholder="add notes..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      rows={2}
                      className="forge-notes-area"
                      data-testid="notes-input"
                    />
                    {noteText && onSaveNote && (
                      <Button size="sm" variant="outline" onClick={onSaveNote} disabled={actionLoading} className="w-full">
                        Save Note
                      </Button>
                    )}
                  </div>
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection
                  title={
                    <div className="flex items-center gap-2">
                      <span>My translation:</span>
                      {lockedFields.includes('translation_en') && (
                        <Lock size={12} className="text-amber-500" title="Translation locked — protected from AI updates" />
                      )}
                    </div>
                  }
                  defaultOpen
                  headerExtra={
                    onToggleFieldLock && (
                      <button
                        className="p-1 rounded hover:bg-muted/50 transition-colors"
                        onClick={(e) => { e.stopPropagation(); onToggleFieldLock('translation_en'); }}
                        title={lockedFields.includes('translation_en') ? 'Unlock translation — AI may update it' : 'Lock translation — protect from AI updates'}
                        data-testid="toggle-lock-translation"
                      >
                        {lockedFields.includes('translation_en') ? <Lock size={14} className="text-amber-500" /> : <Unlock size={14} className="text-muted-foreground" />}
                      </button>
                    )
                  }
                >
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="add translation..."
                        value={translationText}
                        onChange={(e) => setTranslationText(e.target.value)}
                        className="forge-notes-area flex-1"
                        data-testid="translation-input"
                      />
                      {onAutoTranslate && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={onAutoTranslate}
                          disabled={actionLoading}
                          title="Auto translate"
                          data-testid="auto-translate-btn"
                        >
                          <Globe className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {translationText && !translationSaved && onSaveTranslation && (
                      <Button size="sm" variant="outline" onClick={onSaveTranslation} disabled={actionLoading} className="w-full" data-testid="save-translation-btn">
                        Save Translation
                      </Button>
                    )}
                    {translationSaved && (
                      <p className="text-xs text-green-500 text-center">✓ Saved</p>
                    )}
                  </div>
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection
                  title={
                    <span className="inline-flex items-center gap-2">
                      <span>{`Definitions${wordData.definitions?.length ? ` (${wordData.definitions.length})` : ''}:`}</span>
                      {lockedFields.includes('definitions') && (
                        <Lock size={12} className="text-amber-500" title="Field locked — protected from AI updates" />
                      )}
                    </span>
                  }
                  defaultOpen
                  onRefresh={locked ? null : () => onRefreshSection?.('definitions')}
                  headerExtra={
                    <div className="flex items-center gap-1">
                      {onToggleFieldLock && (
                        <button
                          className="p-1 rounded hover:bg-muted/50 transition-colors"
                          onClick={(e) => { e.stopPropagation(); onToggleFieldLock('definitions'); }}
                          title={lockedFields.includes('definitions') ? 'Unlock field' : 'Lock field'}
                          data-testid="toggle-lock-definitions"
                        >
                          {lockedFields.includes('definitions')
                            ? <Lock size={14} className="text-amber-500" />
                            : <Unlock size={14} className="text-muted-foreground" />}
                        </button>
                      )}
                      {!editMode && (onSaveEdit || onSubmitSuggestion) && (
                        <button
                          className="p-1 rounded hover:bg-muted/50 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditMode(true);
                            setEditedDefinition(wordData.definitions?.map(d => d.definition).join('\n') || '');
                          }}
                          title={isAdmin ? 'Edit definitions' : 'Suggest correction'}
                          data-testid="edit-definitions-btn"
                        >
                          <Pencil size={14} className="text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                    </div>
                  }
                >
                  {editMode ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedDefinition}
                        onChange={(e) => setEditedDefinition(e.target.value)}
                        placeholder="Enter definitions (one per line)"
                        rows={4}
                        className="text-sm"
                        data-testid="edit-definitions-textarea"
                      />
                      {!isAdmin && (
                        <Input
                          value={suggestionReason}
                          onChange={(e) => setSuggestionReason(e.target.value)}
                          placeholder="Reason for correction (optional)"
                          className="text-sm"
                          data-testid="suggestion-reason-input"
                        />
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditMode(false); setEditedDefinition(''); setSuggestionReason(''); }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newDefs = editedDefinition
                              .split('\n')
                              .filter(Boolean)
                              .map(d => ({ definition: d.trim(), pos: '' }));
                            if (isAdmin && onSaveEdit) {
                              onSaveEdit('definitions', newDefs);
                            } else if (onSubmitSuggestion) {
                              onSubmitSuggestion('definitions', newDefs, suggestionReason);
                            }
                            setEditMode(false);
                            setEditedDefinition('');
                            setSuggestionReason('');
                          }}
                          disabled={actionLoading || !editedDefinition.trim()}
                          className="flex-1"
                          data-testid="save-definitions-btn"
                        >
                          {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          <span className="ml-1">{isAdmin ? 'Save' : 'Suggest'}</span>
                        </Button>
                      </div>
                      {!isAdmin && (
                        <p className="text-xs text-muted-foreground text-center">
                          Your suggestion will be saved for your view and submitted for admin review
                        </p>
                      )}
                    </div>
                  ) : wordData.definitions?.length > 0 ? (
                    wordData.definitions.map((def, idx) => (
                      <div
                        key={idx}
                        className="forge-field-box"
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 6,
                          cursor: 'pointer',
                          borderLeft: def.preferred ? '3px solid #D4AF37' : '3px solid transparent',
                          paddingLeft: 6,
                        }}
                        onClick={() => onTogglePreferred?.(idx)}
                        title={def.preferred ? 'Click to unpin' : 'Click to pin as preferred'}
                        data-testid={`definition-row-${idx}`}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="forge-entry-line">
                            <span className="forge-pos">{def.pos}</span>
                            <span className="forge-entry-text">{def.definition}</span>
                            {def.preferred && <span style={{ color: '#D4AF37', fontSize: 11, marginLeft: 4 }}>&#9733;</span>}
                          </div>
                          {def.examples?.length > 0 && (
                            <p className="forge-example-inline">&quot;{def.examples[0]}&quot;</p>
                          )}
                        </div>
                        {!locked && onDeleteDefinition && (
                          <button
                            className="forge-remove-btn"
                            title="Delete this definition"
                            onClick={(e) => { e.stopPropagation(); onDeleteDefinition(idx); }}
                            style={{ flexShrink: 0, marginTop: 2 }}
                          >
                            <Minus size={12} />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="forge-empty-hint">No definitions yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* Hide details / AI enrich / Admin unlock */}
                <div className="forge-center-row gap-2 py-2 flex-wrap justify-center">
                  <button
                    className="forge-suggest-btn"
                    onClick={() => setShowDetails(false)}
                    data-testid="hide-details-btn"
                  >
                    Hide details
                  </button>
                  {onRefreshSection && (
                    <button
                      className="forge-suggest-btn"
                      onClick={() => onRefreshSection(null)}
                      disabled={locked}
                      style={locked ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                      data-testid="ai-enrich-btn"
                    >
                      <RefreshCw size={14} style={{ display: 'inline', marginRight: 6 }} />
                      {locked ? 'Locked' : 'AI Enrich All'}
                    </button>
                  )}
                </div>
                {isAdmin && locked && onUnlockWord && (
                  <div className="forge-center-row py-1">
                    <button
                      className="forge-suggest-btn"
                      onClick={onUnlockWord}
                      style={{ backgroundColor: '#dc2626', color: 'white', fontSize: '12px', padding: '4px 12px' }}
                      data-testid="admin-unlock-btn"
                    >
                      <Lock size={13} style={{ display: 'inline', marginRight: 4 }} />
                      Unlock (Admin)
                    </button>
                  </div>
                )}

                {/* Language Issue Report */}
                {onSubmitSuggestion && (
                  !showSuggestionForm ? (
                    <div className="forge-center-row py-1">
                      <button
                        className="text-xs text-muted-foreground hover:text-amber-500 flex items-center gap-1 transition-colors"
                        onClick={() => setShowSuggestionForm(true)}
                        data-testid="report-language-btn"
                      >
                        <AlertTriangle size={12} />
                        Wrong language?
                      </button>
                    </div>
                  ) : (
                    <div className="p-2 bg-amber-500/10 rounded-lg space-y-2 mx-2">
                      <div className="flex items-center gap-2 text-xs text-amber-500">
                        <AlertTriangle size={14} />
                        <span className="font-medium">Report Language Issue</span>
                      </div>
                      <Input
                        value={suggestionReason}
                        onChange={(e) => setSuggestionReason(e.target.value)}
                        placeholder="What is the correct language? (e.g., 'This is Spanish, not Portuguese')"
                        className="text-sm"
                        data-testid="language-issue-input"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowSuggestionForm(false); setSuggestionReason(''); }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-amber-600 hover:bg-amber-700"
                          onClick={() => {
                            if (onSubmitSuggestion && suggestionReason.trim()) {
                              onSubmitSuggestion('language_issue', suggestionReason, `Language misidentification report for "${word}"`);
                            }
                            setShowSuggestionForm(false);
                            setSuggestionReason('');
                          }}
                          disabled={!suggestionReason.trim()}
                          data-testid="submit-language-issue-btn"
                        >
                          <MessageSquare size={14} className="mr-1" />
                          Report
                        </Button>
                      </div>
                    </div>
                  )
                )}

                <div className="forge-divider" />

                <CollapsibleSection
                  title={`Derivations${wordData.derivations?.length ? ` (${wordData.derivations.length})` : ''}:`}
                  onRefresh={locked ? null : () => onRefreshSection?.('derivations')}
                >
                  {wordData.derivations?.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.derivations.slice(0, 10).map((d, idx) => (
                        <a key={idx} href="#" onClick={(e) => onRelatedWordClick?.(d, e)} className="forge-cloud-link" data-testid={`derivation-${idx}`}>{d}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No derivations yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection
                  title={`Example sentences${wordData.examples?.length ? ` (${wordData.examples.length})` : ''}:`}
                  onRefresh={locked ? null : () => onRefreshSection?.('examples')}
                >
                  {wordData.examples?.length > 0 ? (
                    <div className="forge-examples">
                      {wordData.examples.slice(0, 4).map((ex, idx) => (
                        <p key={idx} className="forge-example-line">&quot;{ex}&quot;</p>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No examples yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection
                  title={`Synonyms${wordData.synonyms?.length ? ` (${wordData.synonyms.length})` : ''}:`}
                  onRefresh={locked ? null : () => onRefreshSection?.('synonyms')}
                >
                  {wordData.synonyms?.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.synonyms.slice(0, 10).map((s, idx) => (
                        <a key={idx} href="#" onClick={(e) => onRelatedWordClick?.(s, e)} className="forge-cloud-link" data-testid={`synonym-${idx}`}>{s}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No synonyms yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection
                  title={`Collocates${wordData.collocates?.length ? ` (${wordData.collocates.length})` : ''}:`}
                  onRefresh={locked ? null : () => onRefreshSection?.('collocates')}
                >
                  {wordData.collocates?.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.collocates.slice(0, 10).map((c, idx) => (
                        <a key={idx} href="#" onClick={(e) => onRelatedWordClick?.(c, e)} className="forge-cloud-link" data-testid={`collocate-${idx}`}>{c}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No collocates yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection title={`Categories${wordData.categories?.length ? ` (${wordData.categories.length})` : ''}:`}>
                  {wordData.categories?.length > 0 ? (
                    <div className="space-y-1">
                      {wordData.categories.map((id, idx) => (
                        <div key={idx} className="forge-entry-line">
                          <span className="forge-pos">{id}</span>
                          <span className="forge-entry-text">{WORD_CATEGORIES[id] || `Category ${id}`}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No categories yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection
                  title={`Related words and expressions${wordData.related_expressions?.length ? ` (${wordData.related_expressions.length})` : ''}:`}
                  onRefresh={locked ? null : () => onRefreshSection?.('related_expressions')}
                >
                  {wordData.related_expressions?.length > 0 ? (
                    <div className="forge-link-cloud">
                      {wordData.related_expressions.slice(0, 8).map((expr, idx) => (
                        <a key={idx} href="#" onClick={(e) => onRelatedWordClick?.(expr, e)} className="forge-cloud-link" data-testid={`related-${idx}`}>{expr}</a>
                      ))}
                    </div>
                  ) : (
                    <p className="forge-empty-hint">No related expressions yet</p>
                  )}
                </CollapsibleSection>

                <div className="forge-divider" />

                <CollapsibleSection title="Searches:">
                  <div className="forge-search-buttons">
                    {searchLinks.map((link, idx) => (
                      <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="forge-mini-search-btn" data-testid={`search-${idx}`}>
                        {link.label}
                      </a>
                    ))}
                  </div>
                </CollapsibleSection>

                <div className="forge-divider" />

                {/* ── Biblical / Religious enrichment (Strong's, Frequency, Grammar) ── */}
                {wordData.strongs && (
                  <>
                    <CollapsibleSection title={
                      <span className="text-blue-400">Strong's Concordance</span>
                    } defaultOpen>
                      <div className="space-y-1.5 bg-blue-500/10 rounded-lg p-2.5 border border-blue-500/20 text-xs">
                        {wordData.strongs.number && (
                          <div className="flex items-center gap-2">
                            <span className="text-blue-300 opacity-70">Strong's:</span>
                            <a
                              href={`https://www.blueletterbible.org/lexicon/${wordData.strongs.number.toLowerCase()}/kjv/wlc/0-1/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-blue-400 hover:text-blue-300 underline"
                              data-testid="strongs-link"
                            >{wordData.strongs.number}</a>
                          </div>
                        )}
                        {wordData.strongs.transliteration && (
                          <div className="flex items-center gap-2">
                            <span className="text-blue-300 opacity-70">Translit.:</span>
                            <span className="italic">{wordData.strongs.transliteration}</span>
                          </div>
                        )}
                        {wordData.strongs.pronunciation && (
                          <div className="flex items-center gap-2">
                            <span className="text-blue-300 opacity-70">Pronunc.:</span>
                            <span className="font-mono">{wordData.strongs.pronunciation}</span>
                          </div>
                        )}
                        {wordData.strongs.definition && (
                          <div className="space-y-0.5">
                            <span className="text-blue-300 opacity-70">Definition:</span>
                            <p className="pl-2 border-l-2 border-blue-500/30">{wordData.strongs.definition}</p>
                          </div>
                        )}
                        {wordData.strongs.kjv && (
                          <div className="space-y-0.5">
                            <span className="text-blue-300 opacity-70">KJV:</span>
                            <p className="text-muted-foreground pl-2">{wordData.strongs.kjv}</p>
                          </div>
                        )}
                        {wordData.strongs.derivation && (
                          <div className="space-y-0.5">
                            <span className="text-blue-300 opacity-70">Derivation:</span>
                            <p className="text-muted-foreground italic pl-2">{wordData.strongs.derivation}</p>
                          </div>
                        )}
                      </div>
                    </CollapsibleSection>
                    <div className="forge-divider" />
                  </>
                )}

                {wordData.frequency && (
                  <>
                    <CollapsibleSection title={
                      <span className="text-emerald-400">Word Frequency</span>
                    } defaultOpen>
                      <div className="space-y-1.5 bg-emerald-500/10 rounded-lg p-2.5 border border-emerald-500/20 text-xs">
                        {wordData.frequency.count != null && (
                          <div className="flex items-center justify-between">
                            <span className="text-emerald-300">Occurrences:</span>
                            <span className="font-bold text-emerald-400">{wordData.frequency.count?.toLocaleString()}</span>
                          </div>
                        )}
                        {wordData.frequency.rank != null && (
                          <div className="flex items-center justify-between">
                            <span className="text-emerald-300">Rank:</span>
                            <span className="font-medium">#{wordData.frequency.rank?.toLocaleString()}</span>
                          </div>
                        )}
                        {wordData.frequency.tier && (
                          <div className="flex items-center justify-between">
                            <span className="text-emerald-300">Tier:</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                              wordData.frequency.tier === 'ultra_high' ? 'bg-emerald-500 text-white' :
                              wordData.frequency.tier === 'high' ? 'bg-emerald-400 text-slate-900' :
                              wordData.frequency.tier === 'medium' ? 'bg-yellow-400 text-slate-900' :
                              wordData.frequency.tier === 'low' ? 'bg-orange-400 text-slate-900' :
                              'bg-red-400 text-white'
                            }`}>
                              {wordData.frequency.tier?.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                        )}
                        {wordData.frequency.importance_score != null && (
                          <div className="flex items-center justify-between pt-1 border-t border-emerald-500/20">
                            <span className="text-emerald-300">Priority:</span>
                            <div className="flex items-center gap-1">
                              {[1,2,3,4,5].map(i => (
                                <div key={i} className={`w-1.5 h-1.5 rounded-full ${
                                  i <= Math.ceil(wordData.frequency.importance_score * 5) ? 'bg-emerald-400' : 'bg-slate-600'
                                }`} />
                              ))}
                              <span className="text-emerald-400 ml-1">
                                {wordData.frequency.importance_score >= 0.8 ? 'Essential' :
                                 wordData.frequency.importance_score >= 0.6 ? 'Important' :
                                 wordData.frequency.importance_score >= 0.4 ? 'Useful' : 'Optional'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CollapsibleSection>
                    <div className="forge-divider" />
                  </>
                )}

                {/* ── POS-aware Grammar Analysis (inflection + morphology) ── */}
                {(() => {
                  const inf = wordData.inflection || {};
                  const morph = wordData.morphology || {};
                  const posRaw = (wordData.pos || '').toLowerCase();
                  const isVerb = /verb|vb\b|^v\./.test(posRaw);
                  const isNounAdj = /noun|^n\.|adj/.test(posRaw);
                  const hasInflection = Object.values(inf).some(v => v && v !== 'null' && v !== 'None');
                  const hasLegacy = !!(wordData.root || wordData.stem || wordData.form || wordData.grammar_info ||
                    wordData.person || wordData.tense || wordData.mood || wordData.voice || wordData.gender ||
                    wordData.number || wordData.grammatical_case || morph.stem || morph.form);
                  const hasMorphStructure = !!(morph.word_type || morph.prefix || morph.suffix || morph.root ||
                    morph.compound_components?.length);
                  if (!hasInflection && !hasLegacy && !hasMorphStructure && !wordData.morphology) return null;

                  const InfRow = ({ label, value }) => {
                    if (!value || value === 'null' || value === 'None') return null;
                    return (
                      <div className="flex items-center gap-2">
                        <span className="opacity-60 min-w-[72px]">{label}:</span>
                        <span className="font-medium capitalize">{value}</span>
                      </div>
                    );
                  };

                  return (
                    <>
                      <CollapsibleSection title={
                        <span className="text-purple-400">Grammar Analysis</span>
                      } defaultOpen>
                        <div className="space-y-2 text-xs">
                          {/* Conjugation block (verbs) */}
                          {(isVerb || inf.tense || inf.mood || inf.person || inf.aspect ||
                            wordData.tense || wordData.mood || wordData.person) && (
                            <div className="bg-blue-500/10 rounded-lg p-2.5 border border-blue-500/20">
                              <div className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-1.5">Conjugation</div>
                              <div className="space-y-1">
                                <InfRow label="Tense"   value={inf.tense   || wordData.tense} />
                                <InfRow label="Mood"    value={inf.mood    || wordData.mood} />
                                <InfRow label="Person"  value={inf.person  || wordData.person} />
                                <InfRow label="Number"  value={inf.number  || wordData.number  || morph.number} />
                                <InfRow label="Voice"   value={inf.voice   || wordData.voice} />
                                <InfRow label="Aspect"  value={inf.aspect} />
                              </div>
                            </div>
                          )}

                          {/* Declension block (nouns / adjectives or any word with gender/case) */}
                          {(isNounAdj || inf.gender || inf.case || inf.definiteness ||
                            wordData.gender || wordData.grammatical_case) && (
                            <div className="bg-emerald-500/10 rounded-lg p-2.5 border border-emerald-500/20">
                              <div className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-1.5">Declension</div>
                              <div className="space-y-1">
                                <InfRow label="Gender"       value={inf.gender  || wordData.gender  || morph.gender} />
                                <InfRow label="Number"       value={inf.number  || wordData.number  || morph.number} />
                                <InfRow label="Case"         value={inf.case    || wordData.grammatical_case || morph.case} />
                                <InfRow label="Definiteness" value={inf.definiteness} />
                                <InfRow label="Degree"       value={inf.degree} />
                              </div>
                            </div>
                          )}

                          {/* Morphology structure (word formation) */}
                          {hasMorphStructure && (
                            <div className="bg-purple-500/10 rounded-lg p-2.5 border border-purple-500/20">
                              <div className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider mb-1.5">Word Formation</div>
                              <div className="space-y-1">
                                {(wordData.root || morph.root) && (
                                  <div className="flex items-center gap-2">
                                    <span className="opacity-60 min-w-[72px]">Root:</span>
                                    <span className="font-semibold">{wordData.root || morph.root}</span>
                                    {morph.root_meaning && <span className="text-muted-foreground">— {morph.root_meaning}</span>}
                                  </div>
                                )}
                                {(wordData.stem || morph.stem) && <InfRow label="Stem" value={wordData.stem || morph.stem} />}
                                {(wordData.form || morph.form) && <InfRow label="Form" value={wordData.form || morph.form} />}
                                {morph.prefix && (
                                  <div className="flex items-center gap-2">
                                    <span className="opacity-60 min-w-[72px]">Prefix:</span>
                                    <span className="font-medium">{morph.prefix}</span>
                                    {morph.prefix_meaning && <span className="text-muted-foreground">— {morph.prefix_meaning}</span>}
                                  </div>
                                )}
                                {morph.suffix && (
                                  <div className="flex items-center gap-2">
                                    <span className="opacity-60 min-w-[72px]">Suffix:</span>
                                    <span className="font-medium">{morph.suffix}</span>
                                    {morph.suffix_meaning && <span className="text-muted-foreground">— {morph.suffix_meaning}</span>}
                                  </div>
                                )}
                                {morph.word_type && <InfRow label="Type" value={morph.word_type} />}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleSection>
                      <div className="forge-divider" />
                    </>
                  );
                })()}

                {/* ── In Context (Tier 3) ── */}
                {userTier >= 3 && sentenceText && wordData && !wordData.lookup_pending && (
                  <>
                    <div className="px-1 pb-1">
                      {!inContextResult && (
                        <button
                          data-testid="in-context-btn"
                          disabled={inContextLoading}
                          onClick={async () => {
                            setInContextLoading(true);
                            try {
                              const { data } = await api.post('/vocabulary/in-context', {
                                word: wordData.lemma || token?.normalized_form || word,
                                surface_form: token?.surface_form || word,
                                language_code: langCode,
                                language_name: language?.name || langCode,
                                sentence: sentenceText,
                              });
                              setInContextResult(data);
                            } catch {
                              setInContextResult({ context_definition: 'Analysis failed. Please try again.', grammatical_role: null, inflection_notes: null, literal_vs_contextual: null });
                            } finally {
                              setInContextLoading(false);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 text-xs font-medium hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                        >
                          {inContextLoading
                            ? <><Loader2 className="h-3 w-3 animate-spin" /> Analyzing context…</>
                            : <><Crosshair className="h-3 w-3" /> In Context</>
                          }
                        </button>
                      )}
                      {inContextResult && (
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 p-2.5 text-xs space-y-1.5">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1">
                              <Crosshair className="h-3 w-3" /> In Context
                            </span>
                            <button onClick={() => setInContextResult(null)} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
                          </div>
                          <p className="leading-relaxed">{inContextResult.context_definition}</p>
                          {inContextResult.grammatical_role && (
                            <div className="flex gap-1.5 items-start">
                              <span className="text-amber-400/70 shrink-0">Role:</span>
                              <span className="text-muted-foreground">{inContextResult.grammatical_role}</span>
                            </div>
                          )}
                          {inContextResult.inflection_notes && (
                            <div className="flex gap-1.5 items-start">
                              <span className="text-amber-400/70 shrink-0">Why this form:</span>
                              <span className="text-muted-foreground">{inContextResult.inflection_notes}</span>
                            </div>
                          )}
                          {inContextResult.literal_vs_contextual && (
                            <div className="flex gap-1.5 items-start">
                              <span className="text-amber-400/70 shrink-0">Note:</span>
                              <span className="text-muted-foreground italic">{inContextResult.literal_vs_contextual}</span>
                            </div>
                          )}
                          <button
                            onClick={async () => { setInContextResult(null); setInContextLoading(false); }}
                            className="mt-1 text-[10px] text-amber-400/60 hover:text-amber-400 underline"
                          >re-analyze</button>
                        </div>
                      )}
                    </div>
                    <div className="forge-divider" />
                  </>
                )}

                {/* ── Mastery Panel (enhanced when richer user_state data available) ── */}
                {wordData.user_state && (wordData.user_state.strength != null || wordData.user_state.exposure_count > 0 || wordData.user_state.collocation_mastery != null) && (
                  <>
                    <CollapsibleSection title={
                      <span className="text-muted-foreground">Mastery</span>
                    }>
                      <div className="space-y-2" data-testid="mastery-panel">
                        {/* Strength bar */}
                        <div>
                          <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
                            <span>Memory Strength</span>
                            <span className="font-mono">{Math.round((wordData.user_state.strength || 0) * 100)}%</span>
                          </div>
                          <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500 bg-primary"
                              style={{ width: `${Math.min(100, (wordData.user_state.strength || 0) * 100)}%` }}
                            />
                          </div>
                          {wordData.user_state.cumulative_strength > 0 && wordData.user_state.cumulative_strength !== wordData.user_state.strength && (
                            <div className="text-[9px] text-muted-foreground mt-0.5 font-mono">
                              cumul: {(wordData.user_state.cumulative_strength * 100).toFixed(1)}%
                              {wordData.user_state.stability ? ` | stab: ${wordData.user_state.stability.toFixed(1)}` : ''}
                            </div>
                          )}
                        </div>
                        {/* Collocation mastery */}
                        {wordData.user_state.collocations_total > 0 && (
                          <div>
                            <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
                              <span>Usage Patterns</span>
                              <span className="font-mono">{wordData.user_state.collocations_seen || 0}/{wordData.user_state.collocations_total}</span>
                            </div>
                            <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-500 bg-cyan-500"
                                   style={{ width: `${Math.min(100, (wordData.user_state.collocation_mastery || 0) * 100)}%` }} />
                            </div>
                          </div>
                        )}
                        {/* Stats row */}
                        <div className="flex gap-3 text-[10px] text-muted-foreground flex-wrap">
                          <span><span className="font-mono font-bold text-foreground">{wordData.user_state.exposure_count || 0}</span> exposures</span>
                          {(wordData.user_state.click_count > 0) && (
                            <span><span className="font-mono font-bold text-amber-400">{wordData.user_state.click_count}</span> lookups</span>
                          )}
                          <span><span className="font-mono font-bold text-foreground">{wordData.user_state.unique_contexts || 0}</span> contexts</span>
                        </div>
                        {wordData.user_state.known_in_languages?.length > 0 && (
                          <div className="text-[10px] text-muted-foreground">
                            Known in: <span className="text-emerald-400">{wordData.user_state.known_in_languages.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    </CollapsibleSection>
                    <div className="forge-divider" />
                  </>
                )}

                <CollapsibleSection title="Word data:">
                  <div className="font-mono text-xs opacity-60 whitespace-pre-wrap" data-testid="word-stats">
{`status: ${currentStatus}  strength: ${((wordData.user_state?.strength || 0) * 100).toFixed(0)}%
exposures: ${wordData.user_state?.exposure_count || 0}
correct: ${wordData.user_state?.correct_count || 0}
reviews: ${wordData.user_state?.review_count || 0}`}
                  </div>
                </CollapsibleSection>
              </>
            )}

            {/* Show more details button (when collapsed) */}
            {!showDetails && !bottomExpanded && !isExpressionMode && wordData && (
              <div className="forge-center-row gap-2 py-2">
                <button
                  className="forge-suggest-btn"
                  onClick={() => setShowDetails(true)}
                  data-testid="toggle-details-btn"
                >
                  More details
                </button>
              </div>
            )}

            <div className="forge-divider" />
          </div>

          {/* ── Status footer (always visible, outside scroll area) ── */}
          <footer className="forge-footer flex-shrink-0">
            {isExpressionMode ? (
              <div className="text-center py-3" data-testid="expression-footer">
                <p className="text-sm text-muted-foreground">
                  Expression: <span className="font-medium text-foreground">{word}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Status buttons available for single words only
                </p>
                {onToggleExpressionMode && (
                  <button
                    className="mt-2 text-xs text-muted-foreground hover:text-foreground underline"
                    onClick={() => onToggleExpressionMode(false)}
                    data-testid="exit-expression-mode-btn"
                  >
                    Exit expression mode
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="forge-status-row" data-testid="status-rail" style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className={`forge-status-btn small-round status-x ${currentStatus === 'ignored' ? 'ring-2 ring-white' : ''}`} title="Ignore (X)" onClick={() => onSetStatus('ignored')} disabled={actionLoading} data-testid="status-btn-ignored">
                    <span>&times;</span>
                    <span className="shortcut-hint">X</span>
                  </button>
                  <button className={`forge-status-btn status-new ${currentStatus === 'new' ? 'ring-2 ring-white' : ''}`} onClick={() => onSetStatus('new')} disabled={actionLoading} data-testid="status-btn-new">
                    <span>NEW</span>
                  </button>
                  <button className={`forge-status-btn status-review ${currentStatus === 'learning' ? 'ring-2 ring-white' : ''}`} onClick={() => onSetStatus('learning')} disabled={actionLoading} data-testid="status-btn-learning">
                    <span>Review</span>
                    <span className="shortcut-hint">R</span>
                  </button>
                  <button className={`forge-status-btn status-familiar ${currentStatus === 'familiar' ? 'ring-2 ring-white' : ''}`} onClick={() => onSetStatus('familiar')} disabled={actionLoading} data-testid="status-btn-familiar">
                    <span>Familiar</span>
                    <span className="shortcut-hint">F</span>
                  </button>
                  <button className={`forge-status-btn status-known ${currentStatus === 'known' ? 'ring-2 ring-white' : ''}`} onClick={() => onSetStatus('known')} disabled={actionLoading} data-testid="status-btn-known">
                    <Sparkles size={12} />
                    <span>Known</span>
                    <span className="shortcut-hint">K</span>
                  </button>
                </div>
                {(onToggleExpressionMode || onMarkExpression) && (
                  <div className="flex items-center justify-center mt-2" data-testid="expression-rail">
                    <button
                      className="text-xs text-purple-400 hover:text-purple-300 transition-colors inline-flex items-center gap-1 px-2 py-1 rounded border border-purple-400/20 hover:border-purple-400/50 disabled:opacity-50"
                      onClick={() => {
                        // Prefer in-popover expression mode when the parent supports it;
                        // otherwise fall back to the legacy "mark as expression" flow.
                        if (onToggleExpressionMode) {
                          onToggleExpressionMode(true);
                        } else if (onMarkExpression) {
                          onMarkExpression();
                        }
                      }}
                      disabled={actionLoading}
                      title="Reinforce selection — group this word with surrounding words as an expression"
                      data-testid="mark-expression-btn"
                    >
                      <BookmarkPlus size={12} />
                      <span>Mark as expression…</span>
                    </button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground text-center mt-2 opacity-60">Press X, R, F, or K to set status</p>
              </>
            )}
          </footer>

          {/* Bottom expand/collapse tab — non-persistent, resets on new word */}
          {!isExpressionMode && wordData && (
            <div
              className="forge-bottom-tab"
              onClick={() => setBottomExpanded(b => !b)}
              title={bottomExpanded ? 'Collapse' : 'Expand oracle'}
              data-testid="oracle-bottom-expand-tab"
            >
              <ChevronDown
                size={11}
                style={{ transition: 'transform 0.2s', transform: bottomExpanded ? 'rotate(180deg)' : 'none' }}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
